import {Checkout, ModelContext} from '../../../../types/checkoutApp.types';
import {SelectedShippingOptionModel} from './SelectedShippingOption.model';
import {ShippingOptionModel} from './ShippingOption.model';
import {ShippingOptionFragment} from '../../../../gql/graphql';
import {SPECS} from '../../../../common/constants';

export class TempShippingModel {
  public selectedShippingOption?: SelectedShippingOptionModel;
  public shippingOptions: ShippingOptionModel[];
  public pickupOptions: ShippingOptionModel[];
  public hasPartialShippingOptions: boolean;

  constructor(params: TempShippingModel) {
    this.selectedShippingOption = params.selectedShippingOption;
    this.shippingOptions = params.shippingOptions;
    this.pickupOptions = params.pickupOptions;
    this.hasPartialShippingOptions = params.hasPartialShippingOptions;
  }

  public static fromCheckoutV1(checkout: Checkout, modelContext: ModelContext): TempShippingModel {
    const selectedShippingOption = checkout.shippingInfo?.selectedCarrierServiceOption
      ? new SelectedShippingOptionModel(checkout.shippingInfo?.selectedCarrierServiceOption, modelContext)
      : undefined;

    const allOptions = getFlattenedShippingOptions(checkout);
    const hasPartialShippingOptions = allOptions.some((option) => Boolean(option.partial));
    const selectedShippingOptionCode = checkout.shippingInfo?.selectedCarrierServiceOption?.code ?? undefined;
    const shippingOptions = getOptionModels(
      allOptions.filter((option) => !isPickupOption(option)),
      selectedShippingOptionCode,
      modelContext
    );
    const pickupOptions = getOptionModels(allOptions.filter(isPickupOption), selectedShippingOptionCode, modelContext);
    return new TempShippingModel({
      selectedShippingOption,
      shippingOptions,
      pickupOptions,
      hasPartialShippingOptions,
    });
  }
}

function isPickupOption({logistics}: ShippingOptionFragment): boolean {
  return !!logistics?.pickupDetails;
}

function getFlattenedShippingOptions(checkout: Checkout): (ShippingOptionFragment & {carrierId: string})[] {
  return (
    checkout.shippingInfo?.carrierServiceOptions?.flatMap((carrierServiceOption) =>
      carrierServiceOption?.shippingOptions
        ? (carrierServiceOption.shippingOptions.map((option) => ({
            ...option,
            carrierId: carrierServiceOption.carrierId!,
          })) as (ShippingOptionFragment & {carrierId: string})[])
        : /* istanbul ignore next */ []
    ) ?? []
  );
}

function getOptionModels(
  options: (ShippingOptionFragment & {carrierId: string})[],
  selectedShippingOptionId: string | undefined,
  modelContext: ModelContext
): ShippingOptionModel[] {
  const optionsByCode = options.reduce<{[code: string]: ShippingOptionFragment[]}>((acc, option) => {
    const key = toOptionKey(option);
    return {...acc, [key]: [...(acc[key] ?? []), option]};
  }, {});

  return modelContext.flowAPI.experiments.enabled(SPECS.SupportDeliveryProfilesPhase2)
    ? Object.keys(optionsByCode).map((code) => {
        const [option] = optionsByCode[code];
        if (optionsByCode[code].length === 1) {
          return new ShippingOptionModel(option, undefined, selectedShippingOptionId, modelContext);
        }
        return new ShippingOptionModel(option, optionsByCode[code], selectedShippingOptionId, modelContext);
      })
    : sortedShippingOptions(
        // eslint-disable-next-line sonarjs/no-identical-functions
        Object.keys(optionsByCode).map((code) => {
          const [option] = optionsByCode[code];
          if (optionsByCode[code].length === 1) {
            return new ShippingOptionModel(option, undefined, selectedShippingOptionId, modelContext);
          }
          return new ShippingOptionModel(option, optionsByCode[code], selectedShippingOptionId, modelContext);
        })
      );
}

const toOptionKey = (option: ShippingOptionFragment & {carrierId: string}) => {
  if (!option.logistics?.deliveryTime) {
    return `${option.carrierId}-${option.code}`;
  }
  return `${option.carrierId}-${option.title!}`;
};

function sortedShippingOptions(options: ShippingOptionModel[]): ShippingOptionModel[] {
  return options.sort((a, b) => {
    return a.initialPrice.amount - b.initialPrice.amount;
  });
}
